import React, { useMemo } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "src/components/ui/sheet";
import { Button } from "src/components/ui/button";
import { InvoiceGroup } from "src/redux/billing/billing.types";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getLastMonthAndYear(): { month: number; year: number } {
  const now = new Date();
  const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
  const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  return { month: lastMonth, year };
}

export interface CreateBillingCycleDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingGroups: Pick<InvoiceGroup, "month" | "year">[];
  selectedMonth: number;
  selectedYear: number;
  onMonthChange: (month: number) => void;
  onYearChange: (year: number) => void;
  onCreate: (month: number, year: number) => Promise<void>;
  isCreating: boolean;
}

export const CreateBillingCycleDrawer: React.FC<CreateBillingCycleDrawerProps> = ({
  open,
  onOpenChange,
  existingGroups,
  selectedMonth,
  selectedYear,
  onMonthChange,
  onYearChange,
  onCreate,
  isCreating,
}) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth1Based = now.getMonth() + 1;

  const monthOptions = useMemo(
    () =>
      MONTH_NAMES.map((name, i) => ({
        value: String(i + 1),
        label: name,
      })),
    []
  );

  const yearOptions = useMemo(
    () =>
      Array.from({ length: currentYear - 2020 + 1 }, (_, i) => {
        const y = 2020 + i;
        return { value: String(y), label: String(y) };
      }).reverse(),
    [currentYear]
  );

  const isFuture =
    selectedYear > currentYear ||
    (selectedYear === currentYear && selectedMonth >= currentMonth1Based);

  const cycleExists = existingGroups.some(
    (g) => g.month === selectedMonth && g.year === selectedYear
  );

  const canCreate = !cycleExists && !isFuture;

  const handleCreate = () => {
    if (!canCreate) return;
    void onCreate(selectedMonth, selectedYear);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-white flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>Create billing cycle</SheetTitle>
          <SheetDescription>
            Choose the month and year for the new billing cycle. If a cycle already exists for that period, you will see a message below.
          </SheetDescription>
        </SheetHeader>

        <div className="flex flex-col gap-6 py-6">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <label htmlFor="create-cycle-month" className="text-sm font-medium text-gray-700">
                Month
              </label>
              <select
                id="create-cycle-month"
                value={selectedMonth}
                onChange={(e) => onMonthChange(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {monthOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label htmlFor="create-cycle-year" className="text-sm font-medium text-gray-700">
                Year
              </label>
              <select
                id="create-cycle-year"
                value={selectedYear}
                onChange={(e) => onYearChange(Number(e.target.value))}
                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                {yearOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {isFuture && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              Cannot create a billing cycle for a future month. Only past months are allowed.
            </p>
          )}
          {!isFuture && cycleExists && (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              A billing cycle already exists for {MONTH_NAMES[selectedMonth - 1]} {selectedYear}.
            </p>
          )}
          {canCreate && (
            <Button
              type="button"
              onClick={handleCreate}
              disabled={isCreating}
              className="w-full"
            >
              {isCreating ? "Creating…" : "Create billing cycle"}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CreateBillingCycleDrawer;
